import * as React from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {Dispatch} from 'redux';
import {connect} from 'react-redux';
import {ListGroup, ListGroupItem} from '@openfin/desktop-ui';

import {ROUTES} from '../../../components/Router/consts';
import {UserWorkspaceMeta, UpdatableWorkspaceMeta} from '../../../controller/Workspaces';
import {CreateWorkspace, DeleteWorkspace, RestoreWorkspace, WorkspaceStatus, getWorkspaceStatuses, UpdateWorkspace} from '../../../store/partials/Workspaces';
import {WorkspaceStatusStates} from '../../../types/enums';
import {RootState} from '../../../store/State';
import {uuidv4} from '../../../utils/createUuid';
import {Panel} from '../../components/Panel/Panel';
import {Header} from '../../components/Panel/Header';
import {Footer} from '../../components/Panel/Footer';
import {Icon} from '../../components/Icon/Icon';
import * as WorkspacesIcon from '../../../assets/Workspaces.svg';
import * as PlusCircle from '../../../assets/PlusCircle.svg';

import {WorkspaceListItem, NameValidationState} from './WorkspaceListItem';
import * as Styles from './WorkspacesPanel.module.scss';

interface WorkspacePanelProps extends RouteComponentProps {
    workspaces: UserWorkspaceMeta[];
    workspaceStatuses: WorkspaceStatus;
    createWorkspace: (id: string, name: string) => void;
    restoreWorkspace: (id: string) => void;
    deleteWorkspace: (id: string) => void;
    updateWorkspace: (id: string, meta: UpdatableWorkspaceMeta, shouldUpdateSnapshot: boolean) => void;
}

export const CREATE_MODE_ID = 'create-new-workspace';

const WorkspacesPanelComponent: React.FC<WorkspacePanelProps> = (props) => {
    const {history, workspaces, createWorkspace, restoreWorkspace, deleteWorkspace, workspaceStatuses, updateWorkspace} = props;
    const [createMode, setCreateMode] = React.useState(false);
    const listRef = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
        if (listRef.current) {
            listRef.current.focus();
        }
    }, [listRef]);

    const workspacesSorted = Array.from(workspaces).sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));

    const handleClose = () => {
        history.push(ROUTES.HOME);
    };

    const handleUpdateWorkspace = (id: string, workspaceMeta: UpdatableWorkspaceMeta, shouldUpdateSnapshot: boolean) => {
        updateWorkspace(id, workspaceMeta, shouldUpdateSnapshot);
    };

    const handleRestore = (workspaceId: string) => {
        restoreWorkspace(workspaceId);
    };

    const handleDeleteWorkspace = (workspaceId: string) => {
        deleteWorkspace(workspaceId);
    };

    const handleCreateWorkspace = (id: string, name: string) => {
        createWorkspace(uuidv4(), name);
        setCreateMode(false);
    };

    const validateName = (name: string, id: string) => {
        if (name.trim() === '') {
            return NameValidationState.EMPTY;
        } else if (/[^a-zA-Z0-9 ]/.test(name)) {
            return NameValidationState.INVALID;
        } else if (workspaces.some((userLayout) => userLayout.name.toLocaleLowerCase().localeCompare(name.toLocaleLowerCase()) === 0 && userLayout.id !== id)) {
            return NameValidationState.DUPLICATE;
        }
        return NameValidationState.VALID;
    };

    const listItems = workspacesSorted.map((userLayout) => <ListGroupItem key={userLayout.id} item={userLayout}>
        <WorkspaceListItem
            key={userLayout.id}
            id={userLayout.id}
            name={userLayout.name}
            onDeleteWorkspace={handleDeleteWorkspace}
            onUpdateWorkspace={handleUpdateWorkspace}
            validateWorkspace={validateName}
            onCancelCreation={() => {}}
            onRestoreWorkspace={handleRestore}
            status={workspaceStatuses[userLayout.id] || WorkspaceStatusStates.None}
        />
    </ListGroupItem>);
    if (createMode) {
        listItems.push(<ListGroupItem key={CREATE_MODE_ID} item>
            <WorkspaceListItem
                key={CREATE_MODE_ID}
                id={CREATE_MODE_ID}
                onDeleteWorkspace={handleDeleteWorkspace}
                validateWorkspace={validateName}
                onCreateWorkspace={handleCreateWorkspace}
                onCancelCreation={() => {
                    setCreateMode(false);
                }}
                status={WorkspaceStatusStates.None} />
        </ListGroupItem>);
    }

    return (
        <Panel>
            <Header title="Workspaces" onClose={handleClose} icon={WorkspacesIcon} />
            <div className={Styles['panel-content']}>
                <ListGroup border={false} className={Styles['list']} ref={listRef}>
                    {listItems}
                </ListGroup>
            </div>
            <Footer>
                <Icon className={Styles['footer-icon']} src={PlusCircle} onClick={() => {
                    setCreateMode(true);
                }} />
                <span className={Styles['footer-text']} onClick={() => {
                    setCreateMode(true);
                }}>Create Workspace</span>
            </Footer>
        </Panel>
    );
};

const mapState = (state: RootState) => ({
    workspaces: Object.values(state.workspaces.workspaces),
    workspaceStatuses: getWorkspaceStatuses(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    createWorkspace: (id: string, name: string) => dispatch(new CreateWorkspace(id, name)),
    updateWorkspace: (id: string, meta: UpdatableWorkspaceMeta, shouldUpdateSnapshot: boolean) => dispatch(new UpdateWorkspace(id, meta, shouldUpdateSnapshot)),
    restoreWorkspace: (id: string) => dispatch(new RestoreWorkspace(id)),
    deleteWorkspace: (id: string) => dispatch(new DeleteWorkspace(id))
});

export const WorkspacesPanel = connect(mapState, mapDispatchToProps)(WorkspacesPanelComponent);
